import React, { useCallback, useEffect, useState } from "react";
import { Table } from "../../../../ui/components/Table";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Badge } from "../../../../ui/components/Badge";
import { IconButton } from "../../../../ui/components/IconButton";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherClock,
  FeatherExternalLink,
  FeatherTrash,
  FeatherUpload,
  FeatherX,
} from "@subframe/core";
import { useToast } from "../../../../shared/presentation/components";
import {
  deleteLut,
  getLutSummary,
  getLutUrl,
  listLuts,
  type LutItem,
  type LutSummary,
} from "../../data/api/lutApi";
import { UploadLutModal } from "./UploadLutModal";
import { ItcRegisterSection } from "./ItcRegisterSection";

/** "2026-04-01" → "01 Apr 2026" (empty-safe). */
const fmtDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const ComplianceMastersTab: React.FC = () => {
  const { showError, showSuccess } = useToast();

  const [summary, setSummary] = useState<LutSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [luts, setLuts] = useState<LutItem[]>([]);
  const [loadingLuts, setLoadingLuts] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LutItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      setSummary(await getLutSummary());
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to load LUT status");
    } finally {
      setLoadingSummary(false);
    }
  }, [showError]);

  const fetchLuts = useCallback(async () => {
    setLoadingLuts(true);
    try {
      setLuts(await listLuts());
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to load LUTs");
    } finally {
      setLoadingLuts(false);
    }
  }, [showError]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchSummary(), fetchLuts()]);
  }, [fetchSummary, fetchLuts]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleView = useCallback(
    async (id: number | string) => {
      try {
        const url = await getLutUrl(id);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        showError(err instanceof Error ? err.message : "Failed to open file");
      }
    },
    [showError],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteLut(deleteTarget.id);
      showSuccess("LUT deleted");
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, refresh, showError, showSuccess]);

  const status = summary?.current_status;
  const activeLut = summary?.active_lut ?? null;

  /**
   * ZOUR-90 — an LUT is filed per GSTIN, so a client with several GST
   * registrations needs one per registration per year.
   *
   * The server answers this (`has_multiple` on the summary) rather than the
   * browser counting locations itself, so the table and the coverage line can
   * never disagree with the coverage the server computed.
   *
   * False for every single-registration client — and then the heading copy, the
   * table and the status card are exactly what they were before this ticket.
   */
  const hasMultipleRegistrations = summary?.has_multiple === true;
  const locationCoverage = summary?.locations ?? [];
  const columnCount = hasMultipleRegistrations ? 6 : 5;

  /** The registration a LUT row belongs to. NULL location_id = the primary. */
  const registrationNameFor = (lut: LutItem): string => {
    const stored = (lut.location_id ?? "").trim();

    const match = stored === ""
      ? locationCoverage.find((row) => row.is_primary)
      : locationCoverage.find((row) => row.zoho_location_id === stored);

    // A LUT filed against a registration that is no longer active still has to
    // say something useful, so fall back to the GSTIN on the paper.
    return match?.name ?? lut.gstin ?? "—";
  };

  const renderStatusCard = () => {
    if (loadingSummary && !summary) {
      return (
        <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background py-10">
          <Loader />
        </div>
      );
    }

    // Missing or expired → prominent "upload is pending" banner (red ramp).
    if (status === "missing" || status === "expired") {
      return (
        <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-error-300 bg-error-50 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-error-100">
              <FeatherAlertTriangle className="h-5 w-5 text-error-600" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                LUT upload is pending
              </span>
              <span className="text-body font-body text-subtext-color">
                {/* ZOUR-90 — on a client with several registrations this card
                    must not say "no LUT is on file" when one registration has
                    one. The headline is downgraded whenever ANY registration is
                    uncovered, so the sentence has to name which. */}
                {hasMultipleRegistrations && (summary?.uncovered?.length ?? 0) > 0
                  ? `${summary?.coverage_message ?? ""} Upload one for each registration so its export sales can be raised at 0% IGST.`
                  : status === "expired"
                    ? `Your LUT for ${summary?.current_fy} has lapsed. Upload a valid LUT so export sales can be raised at 0% IGST.`
                    : `No LUT is on file for ${summary?.current_fy}. Upload one so export sales can be raised at 0% IGST.`}
              </span>
            </div>
          </div>
          <div>
            <Button
              icon={<FeatherUpload />}
              onClick={() => setUploadOpen(true)}
            >
              Upload LUT
            </Button>
          </div>
        </div>
      );
    }

    // Expiring soon → gentler notice (blue/warning ramp reads as info here).
    if (status === "expiring_soon") {
      return (
        <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-warning-300 bg-warning-50 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-warning-100">
              <FeatherClock className="h-5 w-5 text-warning-700" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Your LUT expires soon
              </span>
              <span className="text-body font-body text-subtext-color">
                The LUT for {summary?.current_fy} is valid until{" "}
                {fmtDate(activeLut?.valid_to)}
                {typeof summary?.days_to_expiry === "number"
                  ? ` (${summary.days_to_expiry} day${summary.days_to_expiry === 1 ? "" : "s"} left)`
                  : ""}
                . Upload next year's LUT when you have it.
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex flex-col">
              <span className="text-caption font-caption text-subtext-color">
                ARN
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {activeLut?.arn_number || "—"}
              </span>
            </div>
            <Button
              icon={<FeatherUpload />}
              onClick={() => setUploadOpen(true)}
            >
              Upload LUT
            </Button>
          </div>
        </div>
      );
    }

    // Valid → green summary.
    if (status === "valid") {
      return (
        <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-success-200 bg-success-50 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-success-100">
                <FeatherCheckCircle className="h-5 w-5 text-success-700" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  LUT is valid for {summary?.current_fy}
                </span>
                <span className="text-body font-body text-subtext-color">
                  Export / SEZ sales can be raised at 0% IGST.
                </span>
              </div>
            </div>
            <Button
              variant="neutral-secondary"
              icon={<FeatherUpload />}
              onClick={() => setUploadOpen(true)}
            >
              Upload new LUT
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-8">
            <div className="flex flex-col">
              <span className="text-caption font-caption text-subtext-color">
                Financial year
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {activeLut?.financial_year || summary?.current_fy || "—"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-caption font-caption text-subtext-color">
                ARN
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {activeLut?.arn_number || "—"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-caption font-caption text-subtext-color">
                Valid till
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {fmtDate(activeLut?.valid_to)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex w-full flex-col items-start gap-8">
      {/* ── Heading ─────────────────────────────────────────────── */}
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Letter of Undertaking (LUT)
        </span>
        <span className="text-body font-body text-subtext-color">
          {hasMultipleRegistrations
            ? "An LUT is filed per GST registration — one for each, per financial year."
            : "Your company files one LUT per financial year."}
        </span>
      </div>

      {/* ── Current-FY status card ──────────────────────────────── */}
      {renderStatusCard()}

      {/* ZOUR-90 — coverage per GST registration.
          One status for the whole company used to read "valid" to a
          two-registration client holding one LUT, which is the reading that
          lets a branch export go out at 0% with nothing behind it. Shown only
          when there IS more than one registration. */}
      {hasMultipleRegistrations && locationCoverage.length > 0 && (
        <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-default-background p-6">
          <span className="text-body-bold font-body-bold text-default-font">
            LUT by GST registration
          </span>
          {summary?.coverage_message && (
            <span className="text-body font-body text-subtext-color">
              {summary.coverage_message}
            </span>
          )}
          <div className="flex w-full flex-col gap-2">
            {locationCoverage.map((row) => (
              <div
                key={row.zoho_location_id ?? row.name}
                className="flex w-full flex-wrap items-center justify-between gap-2 border-b border-solid border-neutral-border pb-2 last:border-b-0 last:pb-0"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="text-body font-body text-default-font">
                    {row.name}
                    {row.is_primary ? " · Primary" : ""}
                  </span>
                  {row.gstin && (
                    <span className="text-caption font-caption text-subtext-color">
                      {row.gstin}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {row.covered && row.active_lut?.valid_to && (
                    <span className="text-caption font-caption text-subtext-color">
                      valid to {fmtDate(row.active_lut.valid_to)}
                    </span>
                  )}
                  {row.covered ? (
                    <Badge variant="success">LUT on file</Badge>
                  ) : (
                    <Badge variant="error">No LUT</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── History ─────────────────────────────────────────────── */}
      <div className="flex w-full flex-col items-start gap-4">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Past LUTs
        </span>

        <div className="w-full overflow-x-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Financial Year</Table.HeaderCell>
                {hasMultipleRegistrations && (
                  <Table.HeaderCell>Registration</Table.HeaderCell>
                )}
                <Table.HeaderCell>ARN / Number</Table.HeaderCell>
                <Table.HeaderCell>Validity</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>File</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {loadingLuts ? (
              <Table.Row>
                <Table.Cell colSpan={columnCount}>
                  <div className="flex w-full items-center justify-center py-10">
                    <Loader />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : luts.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columnCount}>
                  <div className="flex w-full items-center justify-center py-10">
                    <span className="text-body font-body text-subtext-color">
                      No LUT uploaded yet
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              luts.map((lut) => (
                <Table.Row key={lut.id}>
                  <Table.Cell>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {lut.financial_year || "—"}
                    </span>
                  </Table.Cell>
                  {hasMultipleRegistrations && (
                    <Table.Cell>
                      <div className="flex flex-col">
                        <span className="text-body font-body text-default-font">
                          {registrationNameFor(lut)}
                        </span>
                        {lut.gstin && (
                          <span className="text-caption font-caption text-subtext-color">
                            {lut.gstin}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {lut.arn_number || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {fmtDate(lut.valid_from)} → {fmtDate(lut.valid_to)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {lut.status === "superseded" ? (
                      <Badge variant="neutral">Replaced</Badge>
                    ) : lut.is_currently_valid ? (
                      <Badge variant="success">Valid</Badge>
                    ) : (
                      <Badge variant="warning">Expired</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherExternalLink />}
                        onClick={() => void handleView(lut.id)}
                      >
                        View
                      </Button>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherTrash />}
                        onClick={() => setDeleteTarget(lut)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table>
        </div>
      </div>

      {/* ── Divider ─────────────────────────────────────────────── */}
      <div className="h-px w-full bg-neutral-border" />

      {/* ── Input Tax Credit register (#612) ────────────────────── */}
      <ItcRegisterSection />

      {/* Upload modal */}
      <UploadLutModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        summary={summary}
        onUploaded={() => void refresh()}
      />

      {/* Delete confirm */}
      {deleteTarget && (
        <DialogLayout
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open && !isDeleting) setDeleteTarget(null);
          }}
          className="min-w-[420px] max-w-[480px]"
        >
          <div className="flex w-full flex-col">
            <div className="flex w-full items-start justify-between gap-4 border-b border-solid border-neutral-border p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
                  <FeatherAlertTriangle className="h-5 w-5 text-error-600" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Delete LUT
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Remove the LUT
                    {deleteTarget.financial_year
                      ? ` for ${deleteTarget.financial_year}`
                      : ""}
                    ? This deletes the stored file too.
                  </span>
                </div>
              </div>
              <IconButton
                size="small"
                icon={<FeatherX />}
                onClick={() => {
                  if (!isDeleting) setDeleteTarget(null);
                }}
                disabled={isDeleting}
              />
            </div>
            <div className="flex w-full items-center justify-end gap-3 p-6">
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  if (!isDeleting) setDeleteTarget(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={() => void handleDeleteConfirm()}
                loading={isDeleting}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogLayout>
      )}
    </div>
  );
};
